import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from 'recharts';
import { Eye, TrendingUp, BookOpen, Layers, Mail, CalendarClock } from 'lucide-react';

const CARD = { backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #EFEAE0' };

function StatPill({ Icon, tint, label, value }) {
  return (
    <div className="p-5 rounded-2xl flex items-center gap-4 relative overflow-hidden" style={CARD}>
      <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-10" style={{ backgroundColor: tint }} />
      <div className="p-3 rounded-2xl relative" style={{ backgroundColor: `${tint}20` }}>
        <Icon className="w-5 h-5" style={{ color: tint }} />
      </div>
      <div className="relative">
        <p className="text-[11px] uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', letterSpacing: '0.1em' }}>{label}</p>
        <p className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{value}</p>
      </div>
    </div>
  );
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/analytics');
        if (mounted) setData(data);
      } catch {}
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const sendDigestNow = async () => {
    if (!window.confirm("Send this week's digest email to your admin address right now?")) return;
    setSendingDigest(true);
    try {
      const { data } = await api.post('/admin/analytics-digest/send');
      if (data.ok) toast.success(`Digest emailed — ${data.totals.views} views, ${data.totals.comments} comments (${data.week_range})`);
      else toast.error('Digest send returned an error — check email settings.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send digest');
    } finally { setSendingDigest(false); }
  };

  if (loading) {
    return <div className="p-12 rounded-3xl text-center" style={CARD}>
      <p style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Gathering insights…</p>
    </div>;
  }

  if (!data) {
    return <div className="p-12 rounded-3xl text-center" style={CARD}>
      <p style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Could not load analytics.</p>
    </div>;
  }

  const { top_articles, by_category, trend_14d, totals } = data;
  const hasData = totals.views > 0;

  const trendData = trend_14d.map(t => ({
    label: new Date(t.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    views: t.views,
  }));

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Weekly Digest banner */}
      <div className="p-5 rounded-3xl relative overflow-hidden" style={{ ...CARD, background: 'linear-gradient(135deg, #FDFBF7 0%, #FFFFFF 100%)' }}>
        <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
          <div className="p-2.5 rounded-xl flex-shrink-0" style={{ backgroundColor: '#5B7C9920' }}>
            <CalendarClock className="w-5 h-5" style={{ color: '#5B7C99' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Weekly Trend Digest</h3>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              Every <strong>Monday morning</strong> we'll email you a warm summary — last week's top articles, total views, comments, and where readers are leaning most. Sent automatically to your admin email.
            </p>
          </div>
          <button
            onClick={sendDigestNow}
            disabled={sendingDigest}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full transition hover:-translate-y-0.5 flex-shrink-0"
            style={{ backgroundColor: '#5B7C99', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: 13, opacity: sendingDigest ? 0.6 : 1 }}
            data-testid="send-digest-now-btn"
          >
            <Mail className="w-4 h-4" />
            <span>{sendingDigest ? 'Sending…' : 'Send Now'}</span>
          </button>
        </div>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatPill Icon={Eye} tint="#5B7C99" label="Total Views" value={totals.views.toLocaleString()} />
        <StatPill Icon={BookOpen} tint="#78A18E" label="Published Articles" value={totals.articles} />
        <StatPill Icon={TrendingUp} tint="#E57A44" label="Trend Window" value="Last 14 days" />
      </div>

      {!hasData ? (
        <div className="p-12 rounded-3xl text-center" style={CARD}>
          <Layers className="w-10 h-10 mx-auto mb-3" style={{ color: '#C9C3B4' }} />
          <p style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: '18px', fontWeight: 700 }}>No views yet</p>
          <p className="mt-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            As soon as visitors read your articles, this page will show the topics they lean into most.
          </p>
        </div>
      ) : (
        <>
          {/* Top articles bar chart */}
          <div className="p-6 rounded-3xl" style={CARD}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Top 10 Articles</h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Which readings are resonating the most</p>
              </div>
              <Eye className="w-5 h-5" style={{ color: '#5B7C99' }} />
            </div>
            {top_articles.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(240, top_articles.length * 40)}>
                <BarChart data={top_articles.map(a => ({ name: a.title.length > 40 ? a.title.slice(0, 40) + '…' : a.title, views: a.view_count || 0 }))} layout="vertical" margin={{ left: 8, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE0" horizontal={false} />
                  <XAxis type="number" stroke="#8C867D" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={200} stroke="#595553" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 11 }} tick={{ fill: '#595553' }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E6E2D8', fontFamily: 'Nunito, sans-serif' }} />
                  <Bar dataKey="views" fill="#5B7C99" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>No article views yet.</p>
            )}
          </div>

          {/* Two column: engagement bar + trend line */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Engagement by Area (views + comments + saves per category) */}
            <div className="p-6 rounded-3xl" style={CARD}>
              <div className="mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Engagement by Area</h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Which categories your readers gravitate to — views, comments, and saves combined</p>
              </div>
              {by_category.length > 0 && by_category.some(c => c.engagement > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={by_category.filter(c => c.engagement > 0).map(c => ({
                      name: c.label.length > 16 ? c.label.slice(0, 16) + '…' : c.label,
                      Views: c.views,
                      Comments: c.comments,
                      Saves: c.saves,
                    }))} layout="vertical" margin={{ left: 8, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE0" horizontal={false} />
                      <XAxis type="number" stroke="#8C867D" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 11 }} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" width={130} stroke="#595553" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E6E2D8', fontFamily: 'Nunito, sans-serif' }} />
                      <Legend wrapperStyle={{ fontFamily: 'Nunito, sans-serif', fontSize: 12 }} />
                      <Bar dataKey="Views" stackId="a" fill="#5B7C99" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="Comments" stackId="a" fill="#E57A44" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="Saves" stackId="a" fill="#78A18E" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-1.5">
                    {by_category.filter(c => c.engagement > 0).map((c) => (
                      <div key={c.category} className="flex items-center justify-between text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        <span style={{ color: '#2C2A29', fontWeight: 600 }}>{c.label}</span>
                        <span style={{ color: '#8C867D' }}>
                          <span style={{ color: '#5B7C99' }}>{c.views} views</span> · <span style={{ color: '#E57A44' }}>{c.comments} comments</span> · <span style={{ color: '#78A18E' }}>{c.saves} saves</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="py-12 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                  No engagement yet — as readers view, comment, and save articles, this chart will fill in.
                </p>
              )}
            </div>

            {/* 14-day trend */}
            <div className="p-6 rounded-3xl" style={CARD}>
              <div className="mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>14-Day Trend</h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Daily article views</p>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ left: 0, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE0" />
                  <XAxis dataKey="label" stroke="#8C867D" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 10 }} interval={1} />
                  <YAxis stroke="#8C867D" style={{ fontFamily: 'Nunito, sans-serif', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E6E2D8', fontFamily: 'Nunito, sans-serif' }} />
                  <Line type="monotone" dataKey="views" stroke="#78A18E" strokeWidth={2.5} dot={{ fill: '#78A18E', r: 3 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs mt-2 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                Trend starts collecting from the moment view-tracking was enabled. Earlier days may show 0.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
